import { useState, useEffect } from "react"
import "react-datepicker/dist/react-datepicker.css"
import InsightDataHelper from "./insight-data-helper"
import {Category} from "../../../domain/category"
import {useServices} from "../../../services/services"
import {MonthlySpendingData} from "../../../services/api/insight-api"
import {ChartRange, getDateRange, getFullMonthRange} from "./chart-scope-settings/chart-scope-settings"
import {TransactionRange} from "../../../domain/transaction-range"

export default function useSpendingData(chartRange: ChartRange, applicableCategories: Category[]): [InsightDataHelper | null, InsightDataHelper | null, TransactionRange | null, () => void] {
  const [transactionRange, setTransactionRange] = useState<TransactionRange | null | undefined>(undefined)
  const [monthlySpendingData, setMonthlySpendingData] = useState<MonthlySpendingData | null>(null)
  const [allMonthlySpendingData, setAllMonthlySpendingData] = useState<MonthlySpendingData | null>(null)
  const [spendingData, setSpendingData] = useState<InsightDataHelper | null>(null)
  const [allSpendingData, setAllSpendingData] = useState<InsightDataHelper | null>(null)
  const services = useServices()

  const controller = new AbortController()

  const refreshSpendingData = async () => {
    if (services != null && transactionRange !== undefined && transactionRange !== null) {
      const updateMonthlySpendingData = async () => {
        const [first, last] = getDateRange(chartRange, transactionRange)
        const data = await services.insightApi.getMonthlySpending(first ?? undefined, last ?? undefined, controller.signal)
        await setMonthlySpendingData(data)
      }

      const updateAllMonthlySpendingData = async () => {
        const [first, last] = getFullMonthRange(transactionRange) ?? [null, null]
        const data = await services.insightApi.getMonthlySpending(first ?? undefined, last ?? undefined, controller.signal)
        await setAllMonthlySpendingData(data)
      }

      const task1 = updateMonthlySpendingData()
      const task2 = updateAllMonthlySpendingData()
      await Promise.all([task1, task2])
    }
  }

  useEffect(() => {
    // noinspection JSIgnoredPromiseFromCall
    services?.transactionsApi.getTransactionRange(controller.signal)
      .then((range) => {
        setTransactionRange(range)
      })

    return () => controller.abort()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [services])

  useEffect(() => {
    // noinspection JSIgnoredPromiseFromCall
    refreshSpendingData()

    return () => controller.abort()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartRange, services, transactionRange])

  useEffect(() => {
    if (monthlySpendingData != null && applicableCategories != null && applicableCategories.length > 0) {
      setSpendingData(new InsightDataHelper(monthlySpendingData, applicableCategories))
    }
  }, [monthlySpendingData, applicableCategories])

  useEffect(() => {
    if (allMonthlySpendingData != null && applicableCategories != null && applicableCategories.length > 0) {
      setAllSpendingData(new InsightDataHelper(allMonthlySpendingData, applicableCategories))
    }
  }, [allMonthlySpendingData, applicableCategories])

  return [spendingData, allSpendingData, transactionRange ?? null, refreshSpendingData]
}